import { Ref, useEffect, useEffectEvent, useState } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { ItemNoAndTypeWithOrderedDetails } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import LoadingCreatorFragment from "../InvalidCreatorFragment/InvalidCreatorFragment";
import { ListItem } from "../../../SelectListItem/SelectListItem";
import { ArticlesPrintPtagsQuantity } from "../../../ArticlesPrintPtagsQuantity/ArticlesPrintPtagsQuantity";
import { initializeOrderedDetails } from "@coworker/apprestructured/src/task-management/util";

interface VisibilityCondition {
  field: string;
  value: any;
  operator: "in";
}

export interface ArticlesPrintPtagsCreatorFragmentConfigProps {
  title: string;
  field: string;
  visibility: VisibilityCondition;
}

interface ArticlesPrintPtagsCreatorFragmentProps {
  configProps: ArticlesPrintPtagsCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
}

function shouldShowField(
  task: CreatorFragmentCommonProps["task"],
  visibility: VisibilityCondition
): boolean {
  const currentValue = getByPath(task, visibility.field) as ListItem | null;

  if (visibility.operator === "in") {
    return Array.isArray(visibility.value)
      ? visibility.value.includes(currentValue?.value)
      : false;
  }

  return true;
}

const ArticlesPrintPtagsCreatorFragment = ({
  configProps,
  commonProps,
}: ArticlesPrintPtagsCreatorFragmentProps) => {
  const { field, visibility } = configProps;
  const { task, taskType, onTaskChange } = commonProps;

  const taskItems = validateInitialDetailsFieldValue<
    ItemNoAndTypeWithOrderedDetails[]
  >(getByPath(task, field), field, taskType);

  const initializedTaskItems = initializeOrderedDetails(taskItems);

  const ensureOrderedDetailsInitialized = useEffectEvent(() => {
    if (!Array.isArray(taskItems)) {
      return;
    }

    const hasMissingOrderedDetails = taskItems.some(
      (item) => !item.pieces_ordered || !item.ptags_ordered
    );

    if (hasMissingOrderedDetails && Array.isArray(initializedTaskItems)) {
      onTaskChange(setByPath("details.items", initializedTaskItems));
    }
  });

  useEffect(() => {
    ensureOrderedDetailsInitialized();
  }, []);

  const [ptagsQuantityOverrides, setPtagsQuantityOverrides] = useState<
    Record<string, number>
  >({});

  const itemsWithPtagsOverrides = Array.isArray(initializedTaskItems)
    ? initializedTaskItems.map((item) => ({
        ...item,
        ptags_ordered: {
          quantity:
            ptagsQuantityOverrides[item.no] ?? item.ptags_ordered.quantity,
          completed: item.ptags_ordered.completed,
        },
      }))
    : initializedTaskItems;

  const { items, isFetchingItems } = useAllAttachedItems(
    itemsWithPtagsOverrides ?? LOADING
  );

  const handlePrintPtagsQuantityChange = (
    itemNo: string,
    newPrintPtagsQuantity: number
  ) =>
    setPtagsQuantityOverrides((prev) => ({
      ...prev,
      [itemNo]: newPrintPtagsQuantity,
    }));

  const handleConfirm = () => {
    if (Array.isArray(itemsWithPtagsOverrides)) {
      onTaskChange(setByPath("details.items", itemsWithPtagsOverrides));
    }
  };

  if (!shouldShowField(task, visibility)) {
    return null;
  }

  if (isFetchingItems || isLoading(itemsWithPtagsOverrides)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <ArticlesPrintPtagsQuantity
      itemNosAndTypeWithOrderedDetails={itemsWithPtagsOverrides}
      items={items}
      onPrintPtagsQuantityChange={handlePrintPtagsQuantityChange}
      handleConfirm={handleConfirm}
    />
  );
};

export default ArticlesPrintPtagsCreatorFragment;
